import * as React from 'react';
import moment from 'moment';
import styled, {css} from 'styled-components';
import {v4 as uuid} from 'uuid';
import {darken, lighten, transparentize} from 'polished';
import CourseBuilderContext from 'components/CourseBuilder/CourseBuilderContext';
import {ChapterContentType, ChapterObject, GrowplaceProgram} from 'api/course';
import {LessonObject} from 'api/lesson/types';
import {
  Draggable,
  DraggableProvided,
  DraggableStateSnapshot,
  Droppable,
  DroppableStateSnapshot,
} from 'react-beautiful-dnd';
import TextareaAutosize from 'react-autosize-textarea';
import BoxContainer from 'containers/BoxContainer';
import SlideDown from 'react-slidedown';
import {WebinarObject} from 'api/webinar/types';
import RenderContentElements from './RenderContentElements';
import DragControl from 'components/DragControl';
import ControlButton from 'components/ControlButton';
import InstancePicker, {InstanceType} from 'components/InstancePicker';
import AddButton from './AddButton';
import useModal from 'hooks/use-modal';
import {transition} from 'helpers/css';
import {useTotalToggle} from 'hooks/useTotalToggle';
import {CHAPTER_CONTENT} from '../..';
import {
  IconBackspace,
  IconChevronBottom,
  IconCopy,
  IconLock,
  IconTrash,
} from 'icons';
import {useDrawer} from 'components/Drawer';
import LessonBuilder from 'components/LessonBuilder';
import {WebinarBuilder} from '../../../../../../../WebinarBuilder';
import {TrainingBuilder} from '../../../../../../../TrainingBuilder';
import {FoodBuilder} from '../../../../../../../FoodBuilder';

export type ChapterProps = ChapterObject & {
  index: number;
};

const Wrapper = styled.div`
  margin-bottom: 10px;
`;

const Container = styled(BoxContainer)<DraggableStateSnapshot>`
  ${({isDragging, isDropAnimating}) => isDragging && !isDropAnimating && css`
    box-shadow: 3px 24px 60px rgba(25, 25, 27, .5);
  `};
`;

const Header = styled.header`
  box-sizing: border-box;
  padding: 10px;
  
  gap: 4px;
  justify-content: space-between;
  align-items: flex-start;
  grid-template-columns: min-content 1fr min-content;
  display: grid;
`;

const Controls = styled.nav`
  align-items: center;
  grid-auto-flow: column;
  display: inline-grid;
`;

const Information = styled.div`
  gap: 2px;
  display: grid;
`;

const Timestamp = styled.span`
  margin-left: 8px;
  color: ${({theme}) => `
    ${transparentize(.6, theme.variables.color)};
  `};
  text-transform: capitalize;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
`;

const Title = styled(TextareaAutosize)`
  resize: none;
  padding: 4px 6px;
  border-radius: 5px;
  border: 1px solid transparent;
  
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  
  &::placeholder {
    color: ${({theme}) => `
      ${transparentize(.6, theme.variables.color)};
    `};
  }
  
  &:focus {
    border-color: ${({theme}) => `
      ${transparentize(.92, theme.colors.white)};
    `}
    
    background: ${({theme}) => `
      ${lighten(.05, theme.variables.accentBackgroundColor)};
    `};
    
    &::placeholder {
      opacity: 0;
    }
  }
  
  &, &::placeholder {
    ${transition()};
  }
`;

const ToggleContainer = styled.div<{
  isToggle: boolean;
}>`
  display: grid;
  ${transition()};
  
  ${({isToggle}) => isToggle && css`
    color: ${({theme}) => `
      ${transparentize(.2, theme.variables.color)};
    `};
    transform: rotate(-180deg);
  `};
`;

const Content = styled.div<DroppableStateSnapshot>`
  ${transition()};
  ${({isDraggingOver, draggingFromThisWith}) => (isDraggingOver || draggingFromThisWith) && css`
    background: ${({theme}) => `
      ${darken(.02, theme.variables.accentBackgroundColor)};
    `};
  `};
`;

const Footer = styled.footer`
  column-gap: 6px;
  grid-auto-flow: column;
  justify-content: flex-start;
  display: grid;
  border-top: 1px solid ${({theme}) => theme.variables.borderColor};
  box-sizing: border-box;
  padding: 16px 20px;
`;

/**
 * This component renders the chapter setting.
 * @param {ChapterProps} props
 * @constructor
 */
export default function Chapter(
    props: ChapterProps,
): React.ReactElement<ChapterProps> {
  const lessonPicker = useModal();
  const webinarPicker = useModal();
  const trainingPicker = useModal();
  const foodPicker = useModal();
  const lessonDrawer = useDrawer({
    align: 'right',
  });
  const webinarDrawer = useDrawer({
    align: 'right',
  });
  const trainingDrawer = useDrawer({
    align: 'right',
  });
  const foodDrawer = useDrawer({
    align: 'right',
  });

  const {
    course,
    setCourse,
  } = React.useContext(CourseBuilderContext);

  const {
    isTotalToggle,
    setTotalToggle,
  } = useTotalToggle().useContext();

  const [
    isToggle,
    setToggle,
  ] = React.useState<boolean>(true);

  /**
   * This function renders the header of the component.
   * @param {DraggableProvided} provided
   * @param {DraggableStateSnapshot} snapshot
   * @return {JSX.Element}
   */
  function renderHeader(
      provided: DraggableProvided,
      snapshot: DraggableStateSnapshot,
  ): JSX.Element {
    /**
     * This function renders the drag control.
     * @return {JSX.Element}
     */
    function renderDragControl(): JSX.Element {
      return (
        <DragControl
          provided={provided}
          snapshot={snapshot}
        />
      );
    }

    /**
     * This function renders the date of the chapter.
     * @return {JSX.Element}
     */
    function renderTimestamp(): JSX.Element | undefined {
      if (!course?.fixedDate?.active) return undefined;

      const startDate = course?.fixedDate?.dates.startDate;
      if (!startDate) return undefined;

      const program = course?.programs;
      const content = program?.content as GrowplaceProgram;
      const chapter = content.chapters.find((chapter) => (
        chapter.id === props.id
      ));

      if (!chapter) return undefined;

      const index = content.chapters.indexOf(chapter);
      const date = moment(startDate).add('days', index);

      return startDate ? (
        <Timestamp>
          {moment(date).format('dddd, DD MMM')}
        </Timestamp>
      ) : undefined;
    }

    /**
     * This function renders the chapter title.
     * @return {JSX.Element}
     */
    function renderTitle(): JSX.Element {
      /**
       * The blur event handler.
       * @param {React.FocusEvent<HTMLTextAreaElement>} event
       * @return {void}
       */
      function handleBlur(event: React.FocusEvent<HTMLTextAreaElement>): void {
        const program = course?.programs;
        const content = program?.content as GrowplaceProgram;
        const chapter = content.chapters.find((chapter) => (
          chapter.id === props.id
        ));

        if (!chapter) return;

        chapter.title = event.target.value;
        (program && setCourse) && setCourse({
          ...course,
          programs: {
            ...program,
            content,
          },
        });
      }

      return (
        <Title
          defaultValue={props.title}
          placeholder="Введите название главы или оставьте поле пустым, чтобы сделать выходной"
          onBlur={handleBlur}
        />
      );
    }

    /**
     * This function renders controls for interaction with the chapter.
     * @return {JSX.Element}
     */
    function renderControls(): JSX.Element {
      /**
       * This function will render the chapter setting control.
       * @return {JSX.Element}
       */
      function renderLock(): JSX.Element {
        return (
          <ControlButton>
            <IconLock />
          </ControlButton>
        );
      }

      /**
       * This function renders a chapter content clearing control.
       * @return {JSX.Element}
       */
      function renderClear(): JSX.Element {
        /**
         * This click event handler.
         * @return {void}
         */
        function handleClick(): void {
          const program = course?.programs;
          const content = program?.content as GrowplaceProgram;
          const chapter = content.chapters.find((chapter) => (
            chapter.id === props.id
          ));

          if (!chapter) return;
          chapter.content = [];

          (program && setCourse) && setCourse({
            ...course,
            programs: {
              ...program,
              content,
            },
          });
        }

        return (
          <ControlButton onClick={handleClick}>
            <IconBackspace />
          </ControlButton>
        );
      }

      /**
       * This function renders a chapter content copy control.
       * @return {JSX.Element}
       */
      function renderCopy(): JSX.Element {
        /**
         * This click event handler.
         * @return {void}
         */
        function handleClick(): void {
          const program = course?.programs;
          const content = program?.content as GrowplaceProgram;
          const chapter = content.chapters.find((chapter) => (
            chapter.id === props.id
          ));

          if (!chapter) return;
          content.chapters.splice(props.index, 0, {
            ...chapter,
            id: uuid(),
          });

          (program && setCourse) && setCourse({
            ...course,
            programs: {
              ...program,
              content,
            },
          });
        }

        return (
          <ControlButton onClick={handleClick}>
            <IconCopy />
          </ControlButton>
        );
      }

      /**
       * This function renders the chapter delete control.
       * @return {JSX.Element}
       */
      function renderTrash(): JSX.Element {
        /**
         * This click event handler.
         * @return {void}
         */
        function handleClick(): void {
          const program = course?.programs;
          const content = program?.content as GrowplaceProgram;

          (program && setCourse) && setCourse({
            ...course,
            programs: {
              ...program,
              content: {
                chapters: content?.chapters.filter((chapter) => (
                  chapter.id !== props.id
                )),
              },
            },
          });
        }

        return (
          <ControlButton onClick={handleClick}>
            <IconTrash />
          </ControlButton>
        );
      }

      /**
       * This function renders the content display switch.
       * @return {JSX.Element}
       */
      function renderToggle(): JSX.Element {
        /**
         * The event when this button is clicked.
         * @return {void}
         */
        function handleClick(): void {
          setTotalToggle(false);
          setToggle(!isToggle);
        }

        return (
          <ControlButton onClick={handleClick}>
            <ToggleContainer isToggle={isToggle || isTotalToggle}>
              <IconChevronBottom />
            </ToggleContainer>
          </ControlButton>
        );
      }

      return (
        <Controls>
          {renderLock()}
          {renderClear()}
          {renderCopy()}
          {renderTrash()}
          {renderToggle()}
        </Controls>
      );
    }

    return (
      <Header>
        {renderDragControl()}
        <Information>
          {renderTimestamp()}
          {renderTitle()}
        </Information>
        {renderControls()}
      </Header>
    );
  }

  /**
   * This function renders the body of the component that contains
   * the dropdown content.
   * @return {JSX.Element}
   */
  function renderBody(): JSX.Element {
    /**
     * This function will render the contents of the chapter.
     * @return {JSX.Element}
     */
    function renderContent(): JSX.Element {
      return (
        <Droppable droppableId={props.id} type={CHAPTER_CONTENT}>
          {(provided, snapshot) => (
            <Content
              ref={provided.innerRef}
              {...provided.droppableProps}
              {...snapshot}
            >
              {props.content && (
                <RenderContentElements
                  content={props.content}
                  chapterId={props.id}
                />
              )}
              {provided.placeholder}
            </Content>
          )}
        </Droppable>
      );
    }

    /**
     * This function renders a footer with buttons for adding content
     * to the chapter.
     * @return {JSX.Element}
     */
    function renderFooter(): JSX.Element {
      /**
       * This function will render a button for adding or creating a lesson.
       * @return {JSX.Element}
       */
      function renderAddLesson(): JSX.Element {
        /**
         * This click event handler.
         * @return {void}
         */
        function handleClick(): void {
          lessonPicker.setModalVisible(true);
        }

        /**
         * This blur lesson picker event handler.
         */
        function handleBlur(): void {
          lessonPicker.setModalVisible(false);
        }

        /**
         * This select event handler.
         * @param {LessonObject<id>[]} selected
         */
        function handleSelect(selected: LessonObject['id'][]): void {
          const program = course?.programs;
          const content = program?.content as GrowplaceProgram;
          const chapter = content.chapters.find((chapter) => (
            chapter.id === props.id
          ));

          if (!chapter) return;

          chapter.content = [
            ...chapter.content || [],
            ...selected.map((id) => ({
              id: uuid(),
              instanceId: id,
              type: ChapterContentType.Lesson,
            })),
          ];

          (program && setCourse) && setCourse({
            ...course,
            programs: {
              ...program,
              content,
            },
          });

          lessonPicker.setModalVisible(false);
        }

        /**
         * The create event handler.
         * @param {LessonObject>} id
         */
        function handleCreate(id: LessonObject['id']): void {
          const program = course?.programs;
          const content = program?.content as GrowplaceProgram;
          const chapter = content.chapters.find((chapter) => (
            chapter.id === props.id
          ));

          if (!chapter) return;

          chapter.content = [
            ...chapter.content || [],
            {
              id: uuid(),
              instanceId: id,
              type: ChapterContentType.Lesson,
            },
          ];

          (program && setCourse) && setCourse({
            ...course,
            programs: {
              ...program,
              content,
            },
          });

          lessonDrawer.close();
        }

        return (
          <React.Fragment>
            <AddButton onAdd={() => lessonDrawer.open()} onClick={handleClick}>
              Урок
            </AddButton>
            {lessonDrawer.render(
              <LessonBuilder
                onCreated={handleCreate}
              />
            )}
            {lessonPicker.isModalVisible && (
              <InstancePicker<LessonObject>
                renderHeader="Выберите урок"
                type={InstanceType.Lesson}
                onBlur={handleBlur}
                onSelect={handleSelect}
                multiply
              />
            )}
          </React.Fragment>
        );
      }

      /**
       * This function will render a button for adding or creating a webinar.
       * @return {JSX.Element}
       */
      function renderAddWebinar(): JSX.Element {
        /**
         * This click event handler.
         * @return {void}
         */
        function handleClick(): void {
          webinarPicker.setModalVisible(true);
        }

        /**
         * This blur webinar picker event handler.
         */
        function handleBlur(): void {
          webinarPicker.setModalVisible(false);
        }

        /**
         * This select event handler.
         * @param {LessonObject<id>[]} selected
         */
        function handleSelect(selected: WebinarObject['id'][]): void {
          const program = course?.programs;
          const content = program?.content as GrowplaceProgram;
          const chapter = content.chapters.find((chapter) => (
            chapter.id === props.id
          ));

          if (!chapter) return;

          chapter.content = [
            ...chapter.content || [],
            ...selected.map((id) => ({
              id: uuid(),
              instanceId: id,
              type: ChapterContentType.Webinar,
            })),
          ];

          (program && setCourse) && setCourse({
            ...course,
            programs: {
              ...program,
              content,
            },
          });

          webinarPicker.setModalVisible(false);
        }

        return (
          <React.Fragment>
            <AddButton onAdd={() => webinarDrawer.open()} onClick={handleClick}>
              Вебинар
            </AddButton>
            {webinarPicker.isModalVisible && (
              <InstancePicker<WebinarObject>
                renderHeader="Выберите вебинар"
                type={InstanceType.Webinar}
                onBlur={handleBlur}
                onSelect={handleSelect}
                multiply
              />
            )}
            {webinarDrawer.render(
              <WebinarBuilder />
            )}
          </React.Fragment>
        );
      }

      /**
       * This function will render a button for adding or creating a training.
       * @return {JSX.Element}
       */
      function renderAddTraining(): JSX.Element {
        /**
         * This click event handler.
         * @return {void}
         */
        function handleClick(): void {
          trainingPicker.setModalVisible(true);
        }

        /**
         * This blur webinar picker event handler.
         */
        function handleBlur(): void {
          trainingPicker.setModalVisible(false);
        }

        /**
         * This select event handler.
         * @param {LessonObject<id>[]} selected
         */
        function handleSelect(selected: WebinarObject['id'][]): void {
          const program = course?.programs;
          const content = program?.content as GrowplaceProgram;
          const chapter = content.chapters.find((chapter) => (
            chapter.id === props.id
          ));

          if (!chapter) return;

          chapter.content = [
            ...chapter.content || [],
            ...selected.map((id) => ({
              id: uuid(),
              instanceId: id,
              type: ChapterContentType.Training,
            })),
          ];

          (program && setCourse) && setCourse({
            ...course,
            programs: {
              ...program,
              content,
            },
          });

          trainingPicker.setModalVisible(false);
        }

        return (
          <React.Fragment>
            <AddButton
              onAdd={() => trainingDrawer.open()}
              onClick={handleClick}
            >
              Тренировка
            </AddButton>
            {trainingPicker.isModalVisible && (
              <InstancePicker
                renderHeader="Выберите тренировку"
                type={InstanceType.Training}
                onBlur={handleBlur}
                onSelect={handleSelect}
                multiply
              />
            )}
            {trainingDrawer.render(
              <TrainingBuilder />
            )}
          </React.Fragment>
        );
      }

      /**
       * This function will render a button for adding or creating a food.
       * @return {JSX.Element}
       */
      function renderAddFood(): JSX.Element {
        /**
         * This click event handler.
         * @return {void}
         */
        function handleClick(): void {
          foodPicker.setModalVisible(true);
        }

        /**
         * This blur webinar picker event handler.
         */
        function handleBlur(): void {
          foodPicker.setModalVisible(false);
        }

        /**
         * This select event handler.
         * @param {LessonObject<id>[]} selected
         */
        function handleSelect(selected: WebinarObject['id'][]): void {
          const program = course?.programs;
          const content = program?.content as GrowplaceProgram;
          const chapter = content.chapters.find((chapter) => (
            chapter.id === props.id
          ));

          if (!chapter) return;

          chapter.content = [
            ...chapter.content || [],
            ...selected.map((id) => ({
              id: uuid(),
              instanceId: id,
              type: ChapterContentType.Food,
            })),
          ];

          (program && setCourse) && setCourse({
            ...course,
            programs: {
              ...program,
              content,
            },
          });

          foodPicker.setModalVisible(false);
        }

        return (
          <React.Fragment>
            <AddButton
              onAdd={() => foodDrawer.open()}
              onClick={handleClick}
            >
              Питание
            </AddButton>
            {foodPicker.isModalVisible && (
              <InstancePicker
                renderHeader="Выберите питание"
                type={InstanceType.Food}
                onBlur={handleBlur}
                onSelect={handleSelect}
                multiply
              />
            )}
            {foodDrawer.render(
              <FoodBuilder />
            )}
          </React.Fragment>
        );
      }

      return (
        <Footer>
          {renderAddLesson()}
          {renderAddWebinar()}
          {renderAddTraining()}
          {renderAddFood()}
        </Footer>
      );
    }

    return (
      <SlideDown
        closed={!isTotalToggle && !isToggle}
        transitionOnAppear={false}
      >
        {renderContent()}
        {renderFooter()}
      </SlideDown>
    );
  }

  return (
    <Draggable draggableId={props.id} index={props.index}>
      {(provided, snapshot) => (
        <Wrapper ref={provided.innerRef} {...provided.draggableProps}>
          <Container {...snapshot}>
            {renderHeader(provided, snapshot)}
            {renderBody()}
          </Container>
        </Wrapper>
      )}
    </Draggable>
  );
}
